import { filterByIdOrShortId } from '@/lib/shortId';
import { supabase } from '@/lib/supabase';
import { paginationRange, buildPaginatedResponse, throwIfError } from '@/lib/supabase-helpers';
import { SeoTrackedKeyword, SeoKeywordSnapshot } from '@/types/api/seo';

function mapKeyword(row: any): SeoTrackedKeyword {
  return {
    id: row.id,
    shortId: row.short_id ?? row.id,
    projectId: row.project_id,
    keyword: row.keyword,
    searchEngine: row.search_engine ?? 'google',
    location: row.location ?? 'us',
    targetPageId: row.target_page_id ?? null,
    createdBy: row.created_by,
    createdAt: row.created_at,
    deletedAt: row.deleted_at,
    latestSnapshot: row.latest_snapshot
      ? mapSnapshot(row.latest_snapshot)
      : null,
  };
}

function mapSnapshot(row: any): SeoKeywordSnapshot {
  return {
    id: row.id,
    keywordId: row.keyword_id,
    position: row.position != null ? Number(row.position) : null,
    previousPosition: row.previous_position != null ? Number(row.previous_position) : null,
    url: row.url,
    searchVolume: row.search_volume != null ? Number(row.search_volume) : null,
    clicks: row.clicks != null ? Number(row.clicks) : null,
    impressions: row.impressions != null ? Number(row.impressions) : null,
    ctr: row.ctr != null ? Number(row.ctr) : null,
    snapshotDate: row.snapshot_date,
  };
}

const seoKeywordsService = {
  async getAll({
    projectId,
    page,
    take,
  }: {
    projectId: string;
    page: number;
    take: number;
  }): Promise<{ data: SeoTrackedKeyword[]; count: number; currentPage: number; lastPage: number; nextPage: number | null; prevPage: number | null }> {
    const [from, to] = paginationRange(page, take);

    // Fetch keywords
    const { data, error, count } = await supabase
      .from('seo_tracked_keywords')
      .select('*', { count: 'exact' })
      .eq('project_id', projectId)
      .is('deleted_at', null)
      .order('keyword', { ascending: true })
      .range(from, to);

    if (error) throw error;

    const keywords = (data ?? []).map(mapKeyword);

    // Fetch the latest snapshot for each keyword in one query
    if (keywords.length > 0) {
      const keywordIds = keywords.map((k) => k.id);
      const { data: snapshots } = await supabase
        .from('seo_keyword_snapshots')
        .select('*')
        .in('keyword_id', keywordIds)
        .order('snapshot_date', { ascending: false });

      if (snapshots && snapshots.length > 0) {
        // Keep only the most recent snapshot per keyword
        const latestByKeyword = new Map<string, any>();
        for (const snap of snapshots) {
          if (!latestByKeyword.has(snap.keyword_id)) {
            latestByKeyword.set(snap.keyword_id, snap);
          }
        }
        for (const kw of keywords) {
          const snap = latestByKeyword.get(kw.id);
          if (snap) {
            kw.latestSnapshot = mapSnapshot(snap);
          }
        }
      }
    }

    return buildPaginatedResponse(keywords, count ?? 0, page, take);
  },

  async getById(id: string): Promise<SeoTrackedKeyword> {
    let query = supabase
      .from('seo_tracked_keywords')
      .select('*');
    query = filterByIdOrShortId(query, id);
    const { data, error } = await query.single();

    if (error) throw error;
    const keyword = mapKeyword(data);

    // Fetch latest snapshot
    const { data: snapshots } = await supabase
      .from('seo_keyword_snapshots')
      .select('*')
      .eq('keyword_id', id)
      .order('snapshot_date', { ascending: false })
      .limit(1);

    if (snapshots && snapshots.length > 0) {
      keyword.latestSnapshot = mapSnapshot(snapshots[0]);
    }

    return keyword;
  },

  async bulkCreate(payload: {
    projectId: string;
    keywords: string[];
    createdBy: string;
    searchEngine?: string;
    location?: string;
  }): Promise<SeoTrackedKeyword[]> {
    const rows = payload.keywords.map((keyword) => ({
      project_id: payload.projectId,
      keyword: keyword.trim(),
      search_engine: payload.searchEngine ?? 'google',
      location: payload.location ?? 'us',
      created_by: payload.createdBy,
      deleted_at: null,
    }));

    const result = await supabase
      .from('seo_tracked_keywords')
      .upsert(rows, { onConflict: 'project_id,keyword,search_engine,location' })
      .select();

    const data = throwIfError(result);
    return (data as any[]).map(mapKeyword);
  },

  async bulkImportFromGSC(payload: {
    projectId: string;
    createdBy: string;
    keywords: Array<{
      keyword: string;
      page: string;
      position: number;
      impressions: number;
      clicks: number;
    }>;
  }): Promise<SeoTrackedKeyword[]> {
    // 1. Upsert keywords
    const keywordRows = payload.keywords.map((kw) => ({
      project_id: payload.projectId,
      keyword: kw.keyword.trim(),
      search_engine: 'google',
      location: 'us',
      created_by: payload.createdBy,
      deleted_at: null,
    }));

    const kwResult = await supabase
      .from('seo_tracked_keywords')
      .upsert(keywordRows, { onConflict: 'project_id,keyword,search_engine,location' })
      .select();

    const insertedKeywords = throwIfError(kwResult) as any[];

    // 2. Build a lookup: keyword text -> id
    const kwMap = new Map<string, string>();
    for (const row of insertedKeywords) {
      kwMap.set(row.keyword, row.id);
    }

    // 3. Insert snapshots with position + URL (skip position 0 — means not ranked)
    const today = new Date().toISOString().split('T')[0];
    const snapshotRows = payload.keywords
      .filter((kw) => kwMap.has(kw.keyword.trim()) && kw.position > 0)
      .map((kw) => ({
        keyword_id: kwMap.get(kw.keyword.trim())!,
        position: Math.round(kw.position * 10) / 10,
        previous_position: null,
        url: kw.page,
        search_volume: kw.impressions,
        snapshot_date: today,
      }));

    if (snapshotRows.length > 0) {
      // Upsert snapshots to avoid duplicates for same keyword+date
      await supabase
        .from('seo_keyword_snapshots')
        .upsert(snapshotRows, { onConflict: 'keyword_id,snapshot_date', ignoreDuplicates: true })
        .select();
    }

    return insertedKeywords.map(mapKeyword);
  },

  async delete(id: string): Promise<SeoTrackedKeyword> {
    const result = await supabase
      .from('seo_tracked_keywords')
      .update({ deleted_at: new Date().toISOString() })
      .eq('id', id)
      .select()
      .single();

    return mapKeyword(throwIfError(result));
  },

  async bulkDelete(ids: string[]): Promise<void> {
    const { error } = await supabase
      .from('seo_tracked_keywords')
      .update({ deleted_at: new Date().toISOString() })
      .in('id', ids);

    if (error) throw error;
  },

  async setTargetPage(keywordId: string, targetPageId: string | null): Promise<void> {
    const { error } = await supabase
      .from('seo_tracked_keywords')
      .update({ target_page_id: targetPageId })
      .eq('id', keywordId);

    if (error) throw error;
  },

  async saveGSCSnapshots(
    keywordId: string,
    dailyData: Array<{ date: string; position: number; clicks: number; impressions: number; ctr: number }>
  ): Promise<void> {
    if (dailyData.length === 0) return;

    // Filter out entries with position 0 (GSC returns 0 when no data — not a real position)
    const validData = dailyData.filter((d) => d.position > 0);
    if (validData.length === 0) return;

    const rows = validData.map((d) => ({
      keyword_id: keywordId,
      position: Math.round(d.position * 10) / 10,
      clicks: d.clicks,
      impressions: d.impressions,
      ctr: d.ctr,
      snapshot_date: d.date,
    }));

    // Batch upsert -- update existing snapshots with new metric data
    for (let i = 0; i < rows.length; i += 50) {
      const chunk = rows.slice(i, i + 50);
      await supabase
        .from('seo_keyword_snapshots')
        .upsert(chunk, { onConflict: 'keyword_id,snapshot_date' });
    }
  },

  async getSnapshots({
    keywordId,
    days = 30,
  }: {
    keywordId: string;
    days?: number;
  }): Promise<SeoKeywordSnapshot[]> {
    const since = new Date();
    since.setDate(since.getDate() - days);

    const { data, error } = await supabase
      .from('seo_keyword_snapshots')
      .select('*')
      .eq('keyword_id', keywordId)
      .gte('snapshot_date', since.toISOString().split('T')[0])
      .order('snapshot_date', { ascending: true });

    if (error) throw error;
    return (data ?? []).map(mapSnapshot);
  },
};

export default seoKeywordsService;
